import { useMemo } from 'react';
import * as R from 'remeda';
import { useIsFetching } from '@tanstack/react-query';
import { Controller, FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@rossum/ui/material';
import {
  SCHEMA_QUERY_KEY,
  SchemaWithEtag,
} from '../../../../business/schema/useSchema';
import { usePatchQueue } from '../../../queues/hooks/usePatchQueue';
import { QUERY_KEY_QUEUE } from '../../../queues/hooks/useQueue';
import { AutomationLevelOptions } from '../core/components/AutomationLevelOptions';
import { useAutomationFlags } from '../core/hooks/useAutomationFlags';
import {
  getSchemaThresholdMap,
  QUALITY_SPOT_CHECK_PERCENTAGE,
} from '../core/utils';
import { LegacyAutomationThresholdsTable } from './components/LegacyAutomationThresholdsTable';
import { LegacyThresholdSlider } from './components/LegacyThresholdSlider';
import { TryAutomationAssistantBanner } from './components/TryAutomationAssistantBanner';
import { useLegacyAutomationSettingsForm } from './hooks/';
import { useUpdateSchemaThresholds } from './hooks/useUpdateSchemaThresholds';
import { LegacyAutomationSettingsFormOutput } from './types';

const HORIZONTAL_SPACE = 3;

const getQualitySpotCheckPercentage = (value: boolean) =>
  value ? QUALITY_SPOT_CHECK_PERCENTAGE : 0;

type LegacyAutomationSettingsProps = {
  queueSchema: SchemaWithEtag | undefined;
  queue: Queue;
  onTryAutomationAssistant: () => void;
  isAuroraQueue: boolean;
};

export const LegacyAutomationSettings = ({
  queue,
  onTryAutomationAssistant,
  queueSchema,
  isAuroraQueue,
}: LegacyAutomationSettingsProps) => {
  const intl = useIntl();

  const { isAutomationAssistantPurchased } = useAutomationFlags();

  const { mutate: patchQueue, isLoading: isPatchingQueue } = usePatchQueue();

  const { handleUpdateSchemaThresholds, isLoading: isPatchingSchema } =
    useUpdateSchemaThresholds({ queueSchema });

  const thresholdMapFromSchema = useMemo(() => {
    const { content } = queueSchema ?? {};
    if (!content) return {};
    return getSchemaThresholdMap(content);
  }, [queueSchema]);

  const defaultValues = useMemo(
    () => ({
      defaultScoreThreshold: queue.defaultScoreThreshold,
      qualitySpotCheckPercentage: queue.qualitySpotCheckPercentage,
      automationLevel: queue.automationLevel,
      thresholdValues: {},
    }),
    [
      queue.qualitySpotCheckPercentage,
      queue.automationLevel,
      queue.defaultScoreThreshold,
    ]
  );
  const formMethods = useLegacyAutomationSettingsForm({
    intl,
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = formMethods;

  const formValues = watch();

  const onSubmit = handleSubmit(data => {
    // useForm does not seem to recognise the type differences post and pre validation of the form values
    const dataPostValidation =
      data as unknown as LegacyAutomationSettingsFormOutput;

    const handlePatchQueue = () => {
      const queueRelatedData = R.omit(dataPostValidation, ['thresholdValues']);
      const hasChanged = !R.isDeepEqual(
        R.omit(defaultValues, ['thresholdValues']),
        queueRelatedData
      );

      if (hasChanged)
        patchQueue(
          {
            queueId: queue.id,
            payload: {
              defaultScoreThreshold: queueRelatedData.defaultScoreThreshold,
              automationLevel: queueRelatedData.automationLevel,
              qualitySpotCheckPercentage:
                queueRelatedData.qualitySpotCheckPercentage,
              automationEnabled: queueRelatedData.automationLevel !== 'never',
            },
          },
          {
            onSuccess: () => {
              reset({ ...queueRelatedData, thresholdValues: {} });
            },
          }
        );
    };

    /**
     * Properly compare thresholds by:
     * 1. Filtering out null/undefined values (only compare fields with actual numeric values)
     * 2. Treating 0 as a valid threshold (different from null which means "no threshold")
     * 3. Checking if any form threshold differs from its corresponding schema value
     */
    const haveThresholdsChanged = (() => {
      // Get form thresholds with non-null values (including 0)
      const formThresholds = R.pickBy(
        dataPostValidation.thresholdValues,
        (value): value is number => value !== null && value !== undefined
      );

      // If no thresholds in form, nothing changed
      if (R.isEmpty(formThresholds)) {
        return false;
      }

      // Check if any form threshold differs from schema threshold
      return R.pipe(formThresholds, R.entries()).some(
        ([schemaId, formValue]) => {
          const schemaValue = thresholdMapFromSchema[schemaId];
          return formValue !== schemaValue;
        }
      );
    })();

    if (haveThresholdsChanged && formValues.automationLevel === 'confident') {
      handleUpdateSchemaThresholds({
        targetStateThresholdsMap: dataPostValidation.thresholdValues,
        onSuccess: () => {
          handlePatchQueue();

          const queueRelatedData = R.omit(dataPostValidation, [
            'thresholdValues',
          ]);
          reset({ ...queueRelatedData, thresholdValues: {} });
        },
      });
    } else handlePatchQueue();
  });

  const isInvalidatingSchema =
    useIsFetching({
      queryKey: [SCHEMA_QUERY_KEY],
    }) > 0;
  const isInvalidatingQueue =
    useIsFetching({
      queryKey: [QUERY_KEY_QUEUE],
    }) > 0;

  const isInvalidating = isInvalidatingSchema || isInvalidatingQueue;

  const isLoading = isInvalidating || isPatchingSchema || isPatchingQueue;

  return (
    <FormProvider {...formMethods}>
      <Stack
        component="form"
        onSubmit={onSubmit}
        sx={{ overflow: 'hidden', py: 3, flexGrow: 1 }}
        spacing={4}
        useFlexGap
      >
        {isAutomationAssistantPurchased && isAuroraQueue ? (
          <Stack sx={{ px: HORIZONTAL_SPACE }}>
            <TryAutomationAssistantBanner onClick={onTryAutomationAssistant} />
          </Stack>
        ) : null}
        <Stack
          sx={{ overflow: 'auto', px: 3, flexGrow: 1 }}
          spacing={3}
          useFlexGap
        >
          <Stack spacing={3} useFlexGap>
            <Stack spacing={2} useFlexGap>
              <Controller
                name="automationLevel"
                control={control}
                render={({ field }) => (
                  <AutomationLevelOptions
                    automationLevel={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                  />
                )}
              />
            </Stack>
            {formValues.automationLevel !== 'never' && (
              <Controller
                name="qualitySpotCheckPercentage"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ margin: 0 }}
                    disabled={isLoading}
                    label={
                      <Stack>
                        <Typography>
                          {intl.formatMessage({
                            id: 'containers.settings.queues.queue.automation.legacySettings.qualitySpotCheck.title',
                          })}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {intl.formatMessage({
                            id: 'containers.settings.queues.queue.automation.legacySettings.qualitySpotCheck.description',
                          })}
                        </Typography>
                      </Stack>
                    }
                    control={
                      <Checkbox
                        checked={field.value > 0}
                        onChange={(_, checked) =>
                          field.onChange(getQualitySpotCheckPercentage(checked))
                        }
                        onBlur={field.onBlur}
                      />
                    }
                  />
                )}
              />
            )}
          </Stack>
          {formValues.automationLevel === 'confident' && (
            <>
              <Controller
                name="defaultScoreThreshold"
                control={control}
                render={({ field }) => (
                  <LegacyThresholdSlider
                    scoreThreshold={field.value}
                    setScoreThreshold={field.onChange}
                    disabled={isLoading}
                  />
                )}
              />
              <Stack sx={{ position: 'relative', flexGrow: 1, minHeight: 400 }}>
                <LegacyAutomationThresholdsTable
                  isLoading={isLoading}
                  queueSchema={queueSchema}
                  defaultScoreThreshold={queue.defaultScoreThreshold}
                />
              </Stack>
            </>
          )}
        </Stack>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading || !isDirty || !isValid}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          sx={{ alignSelf: 'flex-end', mr: HORIZONTAL_SPACE }}
        >
          {intl.formatMessage({
            id: 'containers.settings.queues.queue.automation.legacySettings.saveButton',
          })}
        </Button>
      </Stack>
    </FormProvider>
  );
};
